"use client"

import * as React from "react"
import * as ToastPrimitives from "@radix-ui/react-toast"
import { cva, type VariantProps } from "class-variance-authority"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const ToastProvider = ToastPrimitives.Provider

// Pre-computed base classes for better performance
const TOAST_VIEWPORT_BASE_CLASSES = "fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]";

const TOAST_ACTION_BASE_CLASSES = "inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive";

const TOAST_CLOSE_BASE_CLASSES = "absolute right-2 top-2 rounded-md p-1 text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100 group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-red-600";

const TOAST_TITLE_BASE_CLASSES = "text-sm font-semibold";

const TOAST_DESCRIPTION_BASE_CLASSES = "text-sm opacity-90";

const ToastViewport = React.memo(React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => {
  const memoizedClassName = React.useMemo(() => 
    cn(TOAST_VIEWPORT_BASE_CLASSES, className), 
    [className]
  );

  return (
    <ToastPrimitives.Viewport
      ref={ref}
      className={memoizedClassName}
      {...props}
    />
  );
}));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName

const toastVariants = cva(
  "group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-6 pr-8 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full",
  {
    variants: {
      variant: {
        default: "border bg-background text-foreground",
        destructive:
          "destructive group border-destructive bg-destructive text-destructive-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

const Toast = React.memo(React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
    VariantProps<typeof toastVariants>
>(({ className, variant, ...props }, ref) => {
  const memoizedClassName = React.useMemo(() => 
    cn(toastVariants({ variant }), className), 
    [variant, className]
  );

  return (
    <ToastPrimitives.Root
      ref={ref}
      className={memoizedClassName}
      {...props}
    />
  );
}));
Toast.displayName = ToastPrimitives.Root.displayName

const ToastAction = React.memo(React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => {
  const memoizedClassName = React.useMemo(() => 
    cn(TOAST_ACTION_BASE_CLASSES, className), 
    [className]
  );

  return (
    <ToastPrimitives.Action
      ref={ref}
      className={memoizedClassName}
      {...props}
    />
  );
}));
ToastAction.displayName = ToastPrimitives.Action.displayName

const ToastClose = React.memo(React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => {
  const memoizedClassName = React.useMemo(() => 
    cn(TOAST_CLOSE_BASE_CLASSES, className), 
    [className]
  );

  return (
    <ToastPrimitives.Close
      ref={ref}
      className={memoizedClassName}
      toast-close=""
      {...props}
    >
      <X className="h-4 w-4" />
    </ToastPrimitives.Close>
  );
}));
ToastClose.displayName = ToastPrimitives.Close.displayName

const ToastTitle = React.memo(React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => {
  const memoizedClassName = React.useMemo(() => 
    cn(TOAST_TITLE_BASE_CLASSES, className), 
    [className]
  );

  return (
    <ToastPrimitives.Title
      ref={ref}
      className={memoizedClassName}
      {...props}
    />
  );
}));
ToastTitle.displayName = ToastPrimitives.Title.displayName

const ToastDescription = React.memo(React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => {
  const memoizedClassName = React.useMemo(() => 
    cn(TOAST_DESCRIPTION_BASE_CLASSES, className), 
    [className]
  );

  return (
    <ToastPrimitives.Description
      ref={ref}
      className={memoizedClassName}
      {...props}
    />
  );
}));
ToastDescription.displayName = ToastPrimitives.Description.displayName

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>

type ToastActionElement = React.ReactElement<typeof ToastAction>

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
}
